import React from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/site/Layout';
import { PageHero, CtaBanner } from '../components/site/PageParts';
import { Arrow } from '../components/site/Button';
import useLang from '../i18n/useLang';
import blog1 from '../images/blog1.webp';
import blog2 from '../images/blog2.jpeg';
import blog3 from '../images/blog3.jpeg';
import blog4 from '../images/BlogITvsOT.avif';
import blog6 from '../images/blog6.webp';
import blog7 from '../images/blog7.webp';
import AINoCode from '../images/AINoCode.webp';
import Yuval from '../images/Yuval.png';
import Yaniv from '../images/Yaniv.png';

const authors = {
  yuval: { key: 'blogs.blogMaimPage.Yuval', image: Yuval },
  yaniv: { key: 'blogs.blogMaimPage.Yaniv', image: Yaniv },
};

// Newest first. `only` marks articles written in a single language.
export const posts = [
  {
    link: '/BlogSchneiderElectricHebrew',
    image: blog7,
    only: 'he',
    title: 'מפעל מסורתי שהפך למגדלור עולמי של חדשנות? כן, זה אפשרי.',
    description: '(וזו ההוכחה שתעשייה 4.0 היא לא באזז אלא מציאות מתקדמת)',
    date: '2025-05-18',
    author: 'yuval',
  },
  {
    link: '/BlogManufacturingIntegration',
    image: blog6,
    only: 'en',
    titleKey: 'home.resources.r1',
    descriptionKey: 'home.resources.r1b',
    date: '2025-05-10',
    author: 'yuval',
  },
  {
    link: '/LowCodeWithAi',
    image: AINoCode,
    titleKey: 'blogs.blogMaimPage.LowCodeApplicationDevelopment',
    descriptionKey: 'blogs.blogMaimPage.LowCodeApplicationDevelopmentSUB',
    date: '2024-12-05',
    author: 'yuval',
  },
  {
    link: '/BlogITvsOT',
    image: blog4,
    titleKey: 'blogs.blogMaimPage.ITvsOT',
    descriptionKey: 'blogs.blogMaimPage.ITvsOTSUB',
    date: '2024-11-15',
    author: 'yuval',
  },
  {
    link: '/BlogSapAI',
    image: blog3,
    titleKey: 'blogs.blogMaimPage.SAPAI',
    descriptionKey: 'blogs.blogMaimPage.SAPAISUB',
    date: '2024-11-15',
    author: 'yaniv',
  },
  {
    link: '/BlogSapFiori',
    image: blog2,
    titleKey: 'blogs.blogMaimPage.SAPFiori',
    descriptionKey: 'blogs.blogMaimPage.SAPFioriSUB',
    date: '2024-11-12',
    author: 'yaniv',
  },
  {
    link: '/BlogMesManufacturing',
    image: blog1,
    titleKey: 'blogs.blogMaimPage.MESManufacturing',
    descriptionKey: 'blogs.blogMaimPage.MESManufacturingSUB',
    date: '2024-11-10',
    author: 'yuval',
  },
];

function useFormatDate() {
  const { lang } = useLang();
  const fmt = new Intl.DateTimeFormat(lang === 'he' ? 'he-IL' : 'en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
  return (iso) => fmt.format(new Date(`${iso}T12:00:00`));
}

function PostCard({ post, featured }) {
  const { t, lang } = useLang();
  const formatDate = useFormatDate();
  const title = post.title || t(post.titleKey);
  const description = post.description || t(post.descriptionKey);
  const author = authors[post.author];
  const languageTag = post.only && post.only !== lang ? (post.only === 'he' ? t('home.resources.inHebrew') || 'עברית' : t('home.resources.inEnglish') || 'English') : null;

  return (
    <article className={`group relative flex flex-col ${featured ? 'gap-8 lg:grid lg:grid-cols-[1.25fr_1fr] lg:items-center lg:gap-14' : ''}`}>
      <div className="overflow-hidden rounded-[1.75rem] bg-fog-100 ring-1 ring-fog-200">
        <img
          src={post.image}
          alt=""
          loading={featured ? 'eager' : 'lazy'}
          className={`w-full object-cover transition-transform duration-700 ease-[cubic-bezier(.16,1,.3,1)] group-hover:scale-[1.03] ${featured ? 'aspect-[16/9]' : 'aspect-[16/10]'}`}
        />
      </div>
      <div className={featured ? '' : 'mt-6'}>
        {languageTag && <span className="mb-3 inline-flex rounded-full bg-fog-100 px-2.5 py-1 text-xs font-medium text-ink-600">{languageTag}</span>}
        <h2
          lang={post.only}
          dir={post.only ? (post.only === 'he' ? 'rtl' : 'ltr') : undefined}
          className={`heading text-ink ${featured ? 'text-3xl sm:text-[2.6rem]' : 'text-xl sm:text-2xl'} ${post.only && post.only !== lang ? 'text-start' : ''}`}
        >
          <Link to={post.link} className="after:absolute after:inset-0 after:rounded-[1.75rem]">
            {title}
          </Link>
        </h2>
        <p lang={post.only} className={`mt-3 leading-relaxed text-ink-500 ${featured ? 'text-lg' : 'text-[1rem]'}`}>
          {description}
        </p>
        <div className="mt-5 flex items-center gap-3 text-sm text-ink-500">
          <img src={author.image} alt="" className="h-8 w-8 rounded-full object-cover ring-2 ring-white" />
          <span>
            <span className="font-medium text-ink">{t(author.key)}</span>
            <span className="mx-1.5" aria-hidden="true">·</span>
            <time dateTime={post.date} className="tabular-nums">
              {formatDate(post.date)}
            </time>
          </span>
        </div>
        {featured && (
          <span className="mt-7 inline-flex items-center gap-1.5 font-medium text-grovv-800">
            {t('home.resources.read')}
            <Arrow className="h-4 w-4" />
          </span>
        )}
      </div>
    </article>
  );
}

export default function Blog() {
  const { t } = useLang();
  const [first, ...rest] = posts;

  return (
    <Layout title={t('header.Blog')}>
      <PageHero tone="fog" title={t('pages.blog.title')} lead={t('pages.blog.lead')} />
      <section className="wrap py-20 sm:py-24">
        <PostCard post={first} featured />
        <div className="mt-20 grid grid-cols-1 gap-x-6 gap-y-14 border-t border-fog-200 pt-16 sm:grid-cols-2 lg:grid-cols-3">
          {rest.map((post) => (
            <PostCard key={post.link} post={post} />
          ))}
        </div>
      </section>
      <CtaBanner />
    </Layout>
  );
}
